import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import ImageUploader from "@/components/ImageUploader";
import { X, Lock } from "lucide-react";

const CATEGORIES = ["food", "art", "craft", "vintage", "plants", "music", "wellness", "other"];

const FIELDS = [
  "title", "description", "category", "location", "cover_image",
  "max_vendors", "venue_url", "social_url", "tags", "end_date",
];

function toForm(ev) {
  return {
    title: ev.title || "",
    description: ev.description || "",
    category: ev.category || "craft",
    location: ev.location || "",
    cover_image: ev.cover_image || "",
    max_vendors: ev.max_vendors ?? 20,
    venue_url: ev.venue_url || "",
    social_url: ev.social_url || "",
    tags: Array.isArray(ev.tags) ? ev.tags.join(", ") : (ev.tags || ""),
    end_date: ev.end_date || "",
  };
}

function diffPayload(form, ev) {
  const payload = {};
  const orig = toForm(ev);
  FIELDS.forEach((k) => {
    if (form[k] !== orig[k]) payload[k] = form[k];
  });
  if ("max_vendors" in payload) payload.max_vendors = parseInt(payload.max_vendors, 10) || 20;
  if ("tags" in payload) {
    payload.tags = String(payload.tags).split(",").map((s) => s.trim()).filter(Boolean);
  }
  // Send empty string as null-equivalent for optional url fields — server treats missing as no change,
  // so keep it as-is (empty string is a valid clear).
  return payload;
}

export default function EditEventDialog({ event, open, onOpenChange, onSaved }) {
  const [form, setForm] = useState(() => toForm(event));
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    if (open) setForm(toForm(event));
  }, [open, event]);

  const submit = async (e) => {
    e.preventDefault();
    const payload = diffPayload(form, event);
    if (Object.keys(payload).length === 0) {
      toast.info("Nothing changed");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.patch(`/events/${event.id}`, payload);
      toast.success("Event updated. Applicants have been notified where needed.");
      onSaved?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="edit-event-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl">Edit event</DialogTitle>
          <DialogDescription>
            Some fields are locked once your event is live — they form your promise
            to applicants. Cancel &amp; repost the event if you need to change them.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-5" data-testid="edit-event-form">
          <div
            className="rounded-sm p-3 text-xs border flex items-start gap-2"
            style={{
              backgroundColor: "rgba(31,93,52,0.06)",
              borderColor: "rgba(31,93,52,0.25)",
              color: "var(--ink-soft)",
            }}
            data-testid="edit-event-locked-note"
          >
            <Lock className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" style={{ color: "var(--terracotta)" }} />
            <div>
              <strong>Locked:</strong> Date ({event.date}), City ({event.city}) and Booth fee (${event.booth_fee?.toFixed?.(2) || "0.00"}) can’t be edited — those are the contract with anyone who already applied.
            </div>
          </div>

          <Field label="Title">
            <Input data-testid="edit-title" value={form.title} onChange={upd("title")} required />
          </Field>
          <Field label="Description">
            <Textarea data-testid="edit-description" rows={4} value={form.description} onChange={upd("description")} required />
          </Field>

          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Category">
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="edit-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Max vendors">
              <Input
                type="number" min="1" data-testid="edit-max"
                value={form.max_vendors} onChange={upd("max_vendors")}
              />
            </Field>
          </div>

          <Field label="Venue / location">
            <Input data-testid="edit-location" value={form.location} onChange={upd("location")} required />
          </Field>

          <Field label="Cover image">
            {form.cover_image ? (
              <div className="relative inline-block">
                <img src={form.cover_image} alt="cover" className="max-h-48 rounded-sm" data-testid="edit-cover-preview" />
                <button type="button" onClick={() => setForm({ ...form, cover_image: "" })}
                        className="absolute top-1 right-1 bg-white/90 rounded-full p-1"
                        data-testid="edit-cover-remove">
                  <X className="h-3 w-3" />
                </button>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2 items-center">
                <ImageUploader label="Upload cover" testId="edit-cover" onUploaded={(url) => setForm({ ...form, cover_image: url })} />
                <span className="text-xs" style={{ color: "var(--ink-soft)" }}>or paste URL:</span>
                <Input data-testid="edit-cover-url" value={form.cover_image} onChange={upd("cover_image")} placeholder="https://…" className="flex-1 min-w-[200px]" />
              </div>
            )}
          </Field>

          <Field label="Official venue / event page URL">
            <Input data-testid="edit-venue-url" type="url" value={form.venue_url}
                   onChange={upd("venue_url")} placeholder="https://…" />
          </Field>

          <Field label="Social media link (Instagram, Facebook, TikTok…)">
            <Input data-testid="edit-social-url" type="url" value={form.social_url}
                   onChange={upd("social_url")} placeholder="https://instagram.com/your-event" />
          </Field>

          <Field label="Tags (comma-separated)">
            <Input data-testid="edit-tags" value={form.tags} onChange={upd("tags")}
                   placeholder="handmade, organic, family-friendly" />
          </Field>

          <DialogFooter>
            <Button type="button" variant="outline" data-testid="edit-cancel"
                    onClick={() => onOpenChange(false)} disabled={busy}
                    className="rounded-full">
              Cancel
            </Button>
            <Button type="submit" data-testid="edit-submit" disabled={busy}
                    className="btn-primary rounded-full">
              {busy ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
